import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { UpgradeGate } from "../components/Bits";
import { ZoneStatGrid } from "./BatterZone";
import { SearchSuggest } from "../components/SearchSuggest";
import { Flame } from "lucide-react";

export default function HRRTargets() {
  const [q, setQ] = useState("");
  const { data, isLoading } = useQuery({ queryKey: ["hrr"], queryFn: () => api.get("/hrr").then((r) => r.data) });
  const { data: zone } = useQuery({ queryKey: ["batterZone"], queryFn: () => api.get("/batter-zone").then((r) => r.data) });
  const rows = data?.rows || [];
  const best = [...rows].sort((a, b) => (b.edge + b.hrr_per_game * 0.4) - (a.edge + a.hrr_per_game * 0.4)).slice(0, 5);
  const matches = q.trim().length >= 2
    ? (zone?.rows || []).filter((r) => `${r.name} ${r.team}`.toLowerCase().includes(q.trim().toLowerCase())).slice(0, 5)
    : [];

  return (
    <div className="space-y-5">
      <div className="fade-up">
        <h1 className="font-display text-3xl md:text-4xl font-black uppercase tracking-tight">HRR <span className="gold-text">Targets</span></h1>
        <p className="text-neutral-500 text-sm mt-1">Hits + Runs + RBIs combo props built from each batter's <b className="text-[#a855f7]">last 20 games</b>, refreshed daily.</p>
      </div>

      <div className="fade-up-1" data-testid="hrr-best5">
        <div className="flex items-center gap-2 mb-2">
          <Flame size={16} className="text-[#22c55e]" />
          <h2 className="font-display text-lg font-bold uppercase">Today's 5 <span className="text-[#22c55e]">Best Bets</span></h2>
          <span className="text-xs text-neutral-500">highest efficiency, biggest edge over the line</span>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
          {best.map((r, i) => (
            <Link to={`/research/${r.mlb_id}`} key={r.mlb_id} data-testid={`hrr-best-${r.mlb_id}`}
              className="card-surface rounded-lg p-3.5 hover-lift border-[#22c55e]/30 green-glow">
              <div className="flex items-center gap-2.5">
                <span className="font-data text-lg font-black text-[#22c55e]">{i + 1}</span>
                <img src={r.headshot} alt="" className="w-9 h-9 rounded-full object-cover bg-white/5" loading="lazy" />
                <div className="min-w-0">
                  <div className="font-semibold text-sm truncate">{r.name}</div>
                  <div className="text-[10px] text-neutral-500">{r.team}</div>
                </div>
              </div>
              <div className="mt-2.5 flex items-baseline justify-between">
                <span className="font-data text-sm">Over <b className="text-[#a855f7]">{r.suggested_line}</b></span>
                <span className="font-data text-xs text-[#22c55e] font-bold">+{r.edge} edge</span>
              </div>
              <div className="text-[10px] text-neutral-500 mt-1">{r.hrr_per_game} H+R+RBI per game (L20)</div>
            </Link>
          ))}
        </div>
      </div>

      <div className="card-gold border border-[#a855f7]/30 rounded-xl p-4 fade-up-1" data-testid="hrr-search-section">
        <SearchSuggest value={q} onChange={setQ} testid="hrr-search-input"
          options={[...(zone?.rows || []).map((r) => r.name), ...new Set((zone?.rows || []).map((r) => r.team))]}
          placeholder="Look up any batter's full advanced sheet..." />
        {matches.map((r) => (
          <div key={r.mlb_id} className="mt-3 bg-black/40 border border-white/10 rounded-lg p-3" data-testid={`hrr-search-result-${r.mlb_id}`}>
            <div className="flex items-center gap-2.5 mb-2">
              <img src={r.headshot} alt="" className="w-8 h-8 rounded-full object-cover bg-white/5" loading="lazy" />
              <Link to={`/research/${r.mlb_id}`} className="font-semibold text-sm hover:text-[#a855f7] transition-colors duration-200">{r.name}</Link>
              <span className="text-[10px] text-neutral-500">{r.team} · vs {r.pitcher}</span>
            </div>
            <ZoneStatGrid stats={r.stats} />
          </div>
        ))}
        {q.trim().length >= 2 && matches.length === 0 && <p className="text-xs text-neutral-500 mt-2" data-testid="hrr-search-empty">No batter matches "{q}".</p>}
      </div>

      {isLoading && <div className="text-neutral-500 font-data text-sm animate-pulse">Crunching the combos...</div>}
      <div className="card-surface rounded-xl overflow-x-auto fade-up-1">
        <table className="w-full text-sm min-w-[750px]">
          <thead>
            <tr className="text-neutral-500 text-[11px] uppercase tracking-wider text-left border-b border-white/10">
              <th className="p-3">Player</th><th className="p-3 text-center">G (L20)</th><th className="p-3 text-center">H</th>
              <th className="p-3 text-center">R</th><th className="p-3 text-center">RBI</th>
              <th className="p-3 text-center">H+R+RBI / Game</th><th className="p-3 text-center">Suggested Line</th><th className="p-3 text-center">Edge</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const pace = r.hrr_per_game >= 1.5;
              return (
              <tr key={r.mlb_id} className={`border-b border-white/5 transition-colors duration-200 ${pace ? "bg-[#a855f7]/10 hover:bg-[#a855f7]/15" : "hover:bg-[#1a1a1a]"}`} data-testid={`hrr-row-${r.mlb_id}`}>
                <td className="p-3">
                  <Link to={`/research/${r.mlb_id}`} className="flex items-center gap-2.5 group">
                    <img src={r.headshot} alt="" className="w-8 h-8 rounded-full object-cover bg-white/5" loading="lazy" />
                    <div>
                      <div className="font-medium group-hover:text-[#a855f7] transition-colors duration-200">
                        {r.name}
                        {pace && <Flame size={12} className="inline ml-1.5 text-[#a855f7]" data-testid={`hrr-hot-${r.mlb_id}`} />}
                      </div>
                      <div className="text-[11px] text-neutral-600">{r.team}</div>
                    </div>
                  </Link>
                </td>
                <td className="p-3 text-center font-data text-neutral-400">{r.games}</td>
                <td className="p-3 text-center font-data">{r.hits}</td>
                <td className="p-3 text-center font-data">{r.runs}</td>
                <td className="p-3 text-center font-data">{r.rbi}</td>
                <td className={`p-3 text-center font-data font-bold ${pace ? "text-[#a855f7] drop-shadow-[0_0_6px_rgba(168,85,247,0.6)]" : "text-[#a855f7]"}`}>{r.hrr_per_game}</td>
                <td className="p-3 text-center font-data">Over {r.suggested_line}</td>
                <td className={`p-3 text-center font-data font-bold ${r.edge > 0 ? "text-[#22c55e]" : "text-red-400"}`}>{r.edge > 0 ? "+" : ""}{r.edge}</td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {data?.locked && <UpgradeGate feature={`All ${data.total} HRR combo targets`} />}
      <p className="text-[11px] text-neutral-600">Last-20-game production from the official MLB Stats API. Always compare the suggested line against your book before betting.</p>
    </div>
  );
}
